/**
 * Editor for a list of typed effects (state mutations). Used in two places:
 *   - per-choice `effects` (applied when the choice is picked)
 *   - per-scene `entryEffects` (applied when the learner arrives at a scene)
 *
 * The semantic is identical at both sites; only the placement differs.
 *
 * Each row is `[variable] [op] [value]` where op set and value input are
 * driven by the variable's declared type. Type mismatches can't be authored
 * through the UI — the validator will only catch them from broken imports.
 *
 * Contract:
 *   - `effects` is the current list (may be `undefined`, treated as empty).
 *   - `onChange(newEffects)` is called with the new list. Empty lists are
 *     valid: the parent mutator interprets them as "no effects" and drops
 *     the field from the underlying object so V4 export stays tidy.
 */
import { Plus } from 'lucide-react';
import type { Effect, VariableDecl } from '@branchworks/shared';
import { EffectRow } from './EffectRow';
import { useScenarioStore } from '../../store/scenarioStore';

interface EffectsEditorProps {
  effects: Effect[] | undefined;
  variables: Record<string, VariableDecl>;
  onChange: (newEffects: Effect[]) => void;
  /** Compact mode tightens spacing for nested use inside choice cards. */
  compact?: boolean;
}

export function EffectsEditor({
  effects,
  variables,
  onChange,
  compact = false,
}: EffectsEditorProps) {
  const list = effects || [];
  const variableNames = Object.keys(variables);
  const hasVariables = variableNames.length > 0;
  const setOpenModal = useScenarioStore((s) => s.setOpenModal);

  const addEffect = () => {
    if (!hasVariables) return;
    const firstVar = variableNames[0];
    const decl = variables[firstVar];
    const newEffect: Effect = { variable: firstVar, op: 'set', value: decl.default };
    onChange([...list, newEffect]);
  };

  const updateEffect = (idx: number, patch: Partial<Effect>) => {
    onChange(list.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  const deleteEffect = (idx: number) => {
    onChange(list.filter((_, i) => i !== idx));
  };

  return (
    <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
      {list.map((e, i) => (
        <EffectRow
          key={i}
          effect={e}
          variables={variables}
          onChange={(patch) => updateEffect(i, patch)}
          onDelete={() => deleteEffect(i)}
        />
      ))}

      {!hasVariables && list.length === 0 && (
        <div className="text-xs italic text-ink-3">
          No variables declared.{' '}
          <button
            onClick={() => setOpenModal('variables')}
            className="not-italic underline text-ink-2 hover:text-ink"
          >
            Declare one
          </button>
          {' '}to attach effects.
        </div>
      )}

      {hasVariables && (
        <button
          onClick={addEffect}
          className={`text-xs flex items-center gap-1 hover:underline ${
            list.length === 0 ? 'text-ink-2' : 'text-ink'
          }`}
        >
          <Plus size={11} /> {list.length === 0 ? 'Add effect' : 'Add another'}
        </button>
      )}
    </div>
  );
}
